import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../components';
import { useTranslation } from '../context/TranslationContext';
import { useAuth } from '../context/AuthContext';
import { getModelLanguages } from '../utils/config-calls';
import { getCircuitBreaker, HEALTHCHECK_MODE } from '../utils/circuit-breaker.js';
import GenericTabNavigation from '../components/GenericTabNavigation';
import MessageLog from '../components/MessageLog';
import { useMessageLog } from '../hooks/useMessageLog';
import { useConfigForm } from '../hooks/useConfigForm';
import { useConfigData } from '../hooks/useConfigData';
import { useApiKeyHandlers } from '../hooks/useApiKeyHandlers';
import { UITab, IATab, WorldTab, ComingSoonTab, FormatsTab, ShortcutsTab } from '../components/config-tabs';
import { downloadConfigAsJson, uploadConfigFile } from '../utils/config-import-export';
import { updateRoutingConfigSection, getRoutingConfigSection, setAiWorkingLanguage } from '../utils/routing-config-manager.js';
import { applyTheme } from '../styles/index';
import debug from '@commons-shared/debug';
import { config } from '../config/environment';
import { setItem, getItem } from '../utils/encrypted-local-storage.js';

export default function ConfigPage() {
  const { t, language, changeLanguage, loadPageTranslations } = useTranslation();
  const { isAuthenticated, user } = useAuth();
  const { message, showSuccess, showError, showInfo } = useMessageLog({ autoHide: true, duration: 5000 });
  const [activeTab, setActiveTab] = useState('ui');
  const [forceUpdate, setForceUpdate] = useState(0);

  // Hooks personnalisés
  const { form, getEncryptedApiKey, setEncryptedApiKey, apiKeysLoaded, setApiKeysLoaded, initialApiKeys, setInitialApiKeys } = useConfigForm(language);
  const { languages, models, aiWorkingLanguages, setAiWorkingLanguages } = useConfigData();
  
  const { register, handleSubmit, setValue, watch } = form;

  // Configuration pour observer tous les champs individuellement
  const uiLanguageValue = watch('uiLanguage');
  const uiThemeValue = watch('uiTheme');
  const textAiApiKeyValue = watch('textAiApiKey');
  const visionAiApiKeyValue = watch('visionAiApiKey');
  const translationAiApiKeyValue = watch('translationAiApiKey');
  const textAiModelValue = watch('textAiModel');
  const aiWorkingLanguageValue = watch('aiWorkingLanguage');
  const visionAiModelValue = watch('visionAiModel');
  const translationAiModelValue = watch('translationAiModel');
  
  // Référence pour le watchedtextAiModel utilisé dans d'autres parties du code
  const watchedtextAiModel = textAiModelValue;

  // Calculer les noms des modèles sélectionnés pour les afficher dans les messages
  const getSelectedModelNames = () => {
    const textModel = textAiModelValue && models.text.length > 0 
      ? models.text.find(model => model.model_key === textAiModelValue)
      : null;
    
    const visionModel = visionAiModelValue && models.vision.length > 0
      ? models.vision.find(model => model.model_key === visionAiModelValue)
      : null;
      
    const translationModel = translationAiModelValue && models.translation.length > 0
      ? models.translation.find(model => model.model_key === translationAiModelValue)
      : null;

    return {
      textModelName: textModel?.model_name,
      visionModelName: visionModel?.model_name,
      translationModelName: translationModel?.model_name
    };
  };

  // Hook pour les gestionnaires d'API keys
  const { saveAndTestTextApiKey, saveAndTestVisionApiKey, saveAndTestTranslationApiKey } = useApiKeyHandlers(
    textAiApiKeyValue,
    visionAiApiKeyValue, 
    translationAiApiKeyValue,
    setEncryptedApiKey,
    showSuccess,
    showError,
    t,
    getSelectedModelNames()
  );

  // Référence pour l'input file caché
  const fileInputRef = useRef(null);

  // Charger les clés API encryptées au montage du composant (ne pas décrypter)
  useEffect(() => {
    const loadEncryptedApiKeysInfo = async () => {
      try {
        // Vérifier juste si on a des clés cryptées (sans les décrypter)
        const hasTextKey = localStorage.getItem('encrypted_text_ai_api_key') !== null;
        const hasVisionKey = localStorage.getItem('encrypted_vision_ai_api_key') !== null;
        const hasTranslationKey = localStorage.getItem('encrypted_translation_ai_api_key') !== null;

        setApiKeysLoaded(true);
        debug.log(config, `Clés API cryptées détectées: text=${hasTextKey}, vision=${hasVisionKey}, translation=${hasTranslationKey}`);
      } catch (error) {
        debug.error(config, 'Erreur lors de la vérification des clés API:', error);
        setApiKeysLoaded(true);
      }
    };

    loadEncryptedApiKeysInfo();
  }, []); // Suppression des fonctions de dépendances

  // Synchroniser le formulaire avec le changement de langue du contexte
  useEffect(() => {
    setValue('uiLanguage', language);
    localStorage.setItem('ui_language', language);
    debug.log(config, `Langue UI synchronisée: ${language}`);
    
    if (language) {
      loadPageTranslations('CONFIG').then(() => {
        debug.log(config, `Traductions rechargées pour la langue: ${language}`);
        setForceUpdate(prev => prev + 1);
      });
    }
  }, [language, setValue, loadPageTranslations]);

  // Initialiser le thème au montage du composant
  useEffect(() => {
    const savedTheme = localStorage.getItem('ui_theme') || 'light';
    setValue('uiTheme', savedTheme);
    debug.log(config, `Thème UI initialisé: ${savedTheme}`);
  }, [setValue]);

  // Synchroniser le thème quand la valeur change
  useEffect(() => {
    if (uiThemeValue) {
      localStorage.setItem('ui_theme', uiThemeValue);
      applyTheme(uiThemeValue);
      debug.log(config, `Thème UI appliqué: ${uiThemeValue}`);
    }
  }, [uiThemeValue]);

  // Charger les langues disponibles et gérer la langue actuelle
  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        if (languages.length === 0) return;

        const currentLanguage = localStorage.getItem('ui_language') || language;
        setValue('uiLanguage', currentLanguage);
        
        const isCurrentLanguageAvailable = languages.some(lang => lang.code === currentLanguage);
        
        if (!isCurrentLanguageAvailable && languages.length > 0) {
          const defaultLangAvailable = languages.some(lang => lang.code === 'FR');
          const newLang = defaultLangAvailable ? 'FR' : languages[0].code;
          
          await changeLanguage(newLang);
          setValue('uiLanguage', newLang);
        }
      } catch (error) {
        debug.error(config, 'Error processing languages:', error);
      }
    };
    
    fetchLanguages();
  }, [languages, language, changeLanguage, setValue]);

  // Initialiser la langue de travail IA au démarrage depuis routing_config
  useEffect(() => {
    const loadWorkingLanguage = async () => {
      try {
        const textConfig = await getRoutingConfigSection('text');
        const workingLanguage = textConfig?.['X-Working-Language'];
        if (workingLanguage) {
          setValue('aiWorkingLanguage', workingLanguage);
          debug.log(config, `Langue de travail IA restaurée depuis routing_config: ${workingLanguage}`);
        }
      } catch (error) {
        debug.error(config, 'Erreur lors du chargement de la langue de travail IA:', error);
      }
    };
    
    loadWorkingLanguage();
  }, [setValue]);

  // Synchronisation de la langue de TRAVAIL IA dans routing_config
  // (NE PAS confondre avec ui_language qui est la langue de l'interface)
  useEffect(() => {
    const syncWorkingLanguage = async () => {
      if (aiWorkingLanguageValue) {
        await setAiWorkingLanguage(aiWorkingLanguageValue);
        debug.log(config, `Langue de TRAVAIL IA synchronisée dans routing_config: ${aiWorkingLanguageValue}`);
      }
    };
    syncWorkingLanguage();
  }, [aiWorkingLanguageValue]);

  // Effets pour sauvegarder automatiquement les valeurs dans le localStorage
  useEffect(() => {
    localStorage.setItem('ui_language', uiLanguageValue || '');
  }, [uiLanguageValue]);

  // Synchronisation des modèles Text dans routing_config
  useEffect(() => {
    const syncTextModel = async () => {
      if (textAiModelValue && models.text.length > 0) {
        const selectedModel = models.text.find(model => model.model_key === textAiModelValue);
        if (selectedModel) {
          await updateRoutingConfigSection('text', {
            'X-Model': selectedModel.model_key,
            'X-Router': selectedModel.ia_router || '',
            'X-Router-URL': selectedModel.ia_router_url || ''
          });
          debug.log(config, 'Configuration Text IA synchronisée dans routing_config');
        }
      }
    };
    syncTextModel();
  }, [textAiModelValue, models.text]);

  // Synchronisation des modèles Vision dans routing_config
  useEffect(() => {
    const syncVisionModel = async () => {
      if (visionAiModelValue && models.vision.length > 0) {
        const selectedModel = models.vision.find(model => model.model_key === visionAiModelValue);
        if (selectedModel) {
          await updateRoutingConfigSection('vision', {
            'X-Model': selectedModel.model_key,
            'X-Router': selectedModel.ia_router || '',
            'X-Router-URL': selectedModel.ia_router_url || ''
          });
          debug.log(config, 'Configuration Vision IA synchronisée dans routing_config');
        }
      }
    };
    syncVisionModel();
  }, [visionAiModelValue, models.vision]);

  // Synchronisation des modèles Translation dans routing_config
  useEffect(() => {
    const syncTranslationModel = async () => {
      if (translationAiModelValue && models.translation.length > 0) {
        const selectedModel = models.translation.find(model => model.model_key === translationAiModelValue);
        if (selectedModel) {
          await updateRoutingConfigSection('translation', {
            'X-Model': selectedModel.model_key,
            'X-Router': selectedModel.ia_router || '',
            'X-Router-URL': selectedModel.ia_router_url || ''
          });
          debug.log(config, 'Configuration Translation IA synchronisée dans routing_config');
        }
      }
    };
    syncTranslationModel();
  }, [translationAiModelValue, models.translation]);

  // Les modèles IA et leurs configurations (router, URL) sont maintenant sauvegardés
  // directement dans routing_config par les useEffect de synchronisation ci-dessus.
  // Pas d'initialisation supplémentaire nécessaire.

  // Charger les langues de travail quand le modèle texte change
  useEffect(() => {
    const fetchaiWorkingLanguages = async () => {
      if (!watchedtextAiModel) {
        setAiWorkingLanguages([]);
        return;
      }
      
      try {
        const response = await getModelLanguages(watchedtextAiModel);
        
        if (response.success) {
          const langData = response.languages || [];
          
          if (Array.isArray(langData)) {
            setAiWorkingLanguages([...langData]);
            
            // Charger depuis routing_config (source unique de vérité)
            const routingConfig = JSON.parse(localStorage.getItem('routing_config') || '{}');
            const savedWorkingLang = routingConfig?.text?.['X-Working-Language'];
            if (savedWorkingLang && langData.some(lang => lang.code === savedWorkingLang)) {
              setValue('aiWorkingLanguage', savedWorkingLang);
            } else if (langData.length > 0) {
              setValue('aiWorkingLanguage', langData[0].code);
            }
          } else {
            setAiWorkingLanguages([]);
          }
        } else {
          setAiWorkingLanguages([]);
        }
      } catch (error) {
        debug.error(config, 'Error fetching working languages:', error);
        setAiWorkingLanguages([]);
      }
    };
    
    fetchaiWorkingLanguages();
  }, [watchedtextAiModel, setValue, setAiWorkingLanguages]);

  // Synchroniser la valeur affichée du formulaire quand la liste des langues change
  useEffect(() => {
    if (aiWorkingLanguages.length > 0) {
      const routingConfig = JSON.parse(localStorage.getItem('routing_config') || '{}');
      const savedWorkingLang = routingConfig?.text?.['X-Working-Language'];
      
      // Vérifier si la langue sauvegardée existe dans la nouvelle liste
      if (savedWorkingLang && aiWorkingLanguages.some(lang => lang.code === savedWorkingLang)) {
        // La langue existe, ne rien faire, elle devrait déjà être sélectionnée
        debug.log(config, `Langue sauvegardée ${savedWorkingLang} existe dans la liste`);
      } else if (aiWorkingLanguages.length > 0) {
        // La langue n'existe pas, sélectionner la première de la nouvelle liste
        debug.log(config, `Langue sauvegardée introuvable, sélection de la première: ${aiWorkingLanguages[0].code}`);
        setValue('aiWorkingLanguage', aiWorkingLanguages[0].code);
      }
    }
  }, [aiWorkingLanguages, setValue]);

  // Assurer la synchronisation des valeurs du formulaire lorsque l'onglet change
  useEffect(() => {
    if (activeTab === 'ui') {
      const currentLanguage = localStorage.getItem('ui_language') || language;
      setValue('uiLanguage', currentLanguage);
    }
  }, [activeTab, language, setValue]);

  // Définir la page active dans le contexte de traduction au chargement
  useEffect(() => {
    loadPageTranslations('CONFIG');
  }, [loadPageTranslations]);

  // Gérer le changement de langue
  const handleLanguageChange = async (e) => {
    const newLanguage = e.target.value;
    try {
      if (newLanguage === language) return;
      
      showInfo(t('CONFIG', 'LANGUAGE_CHANGE_LOADING', 'Changement de langue en cours...'));
      await changeLanguage(newLanguage);
      await new Promise(resolve => setTimeout(resolve, 100));
      await loadPageTranslations('CONFIG');
      setForceUpdate(prev => prev + 1);
      
      setTimeout(() => {
        showSuccess(t('CONFIG', 'LANGUAGE_CHANGED', 'Interface language changed'));
      }, 200);
      
    } catch (error) {
      debug.error(config, 'Error during language change:', error);
      showError(t('CONFIG', 'LANGUAGE_CHANGE_ERROR', 'Error changing interface language'));
    }
  };

  // Gérer le changement de thème
  const handleThemeChange = (e) => {
    const newTheme = e.target.value;
    setValue('uiTheme', newTheme);
    showSuccess(t('CONFIG', 'THEME_CHANGED', 'Thème modifié avec succès'));
  };

  // Sauvegarder la configuration
  const onSubmit = (data) => {
    if (data.uiLanguage !== language) {
      changeLanguage(data.uiLanguage);
    }
  };

  // Fonction pour déclencher l'ouverture du sélecteur de fichier
  const triggerFileUpload = () => {
    fileInputRef.current?.click();
  };

  // Charger la configuration depuis un fichier JSON
  const handleUploadConfigFile = (event) => {
    uploadConfigFile(
      event,
      setValue,
      language,
      changeLanguage,
      setEncryptedApiKey,
      getCircuitBreaker,
      HEALTHCHECK_MODE,
      showSuccess,
      showError,
      t
    );
  };

  // Fonction pour rendre le contenu de chaque onglet
  const renderTabContent = () => {
    switch (activeTab) {
      case 'ui':
        return (
          <UITab 
            languages={languages}
            register={register}
            uiLanguageValue={uiLanguageValue}
            handleLanguageChange={handleLanguageChange}
            uiThemeValue={uiThemeValue}
            handleThemeChange={handleThemeChange}
          />
        );
      case 'ia':
        return (
          <IATab 
            models={models}
            register={register}
            watchedtextAiModel={watchedtextAiModel}
            aiWorkingLanguages={aiWorkingLanguages}
            saveAndTestTextApiKey={saveAndTestTextApiKey}
            saveAndTestVisionApiKey={saveAndTestVisionApiKey}
            saveAndTestTranslationApiKey={saveAndTestTranslationApiKey}
            setValue={setValue}
          />
        );
      case 'world':
        return <WorldTab />;
      case 'formats':
        return <FormatsTab />;
      case 'shortcuts':
        return <ShortcutsTab />;
      default:
        return null;
    }
  };
  
  // Configuration des onglets
  const configTabs = React.useMemo(() => [
    { id: 'ui', label: t('CONFIG', 'TAB_UI', 'UI') },
    { id: 'ia', label: t('CONFIG', 'TAB_IA', 'IA') }, 
    { id: 'world', label: t('CONFIG', 'TAB_WORLD', 'World') },
    { id: 'formats', label: t('CONFIG', 'TAB_FORMATS', 'Formats') },
    { id: 'shortcuts', label: t('CONFIG', 'TAB_SHORTCUTS', 'Shortcuts') }
  ], [t, forceUpdate]);
  
  return (
    <div className="mx-auto">
      <form onSubmit={handleSubmit(onSubmit)}>
        <GenericTabNavigation 
          tabs={configTabs}
          activeTab={activeTab} 
          onTabChange={setActiveTab}
        >
          {renderTabContent()}
        </GenericTabNavigation>

        {/* Action Buttons */}
        <div className="flex justify-center space-x-4 mt-8">
          <div className="flex flex-col items-center space-y-2">
            <Button
              variant="secondary"
              onClick={() => downloadConfigAsJson(showSuccess, showError, t)}
            >
              {t('CONFIG', 'CONFIG_DOWNLOAD', 'Download as file')}
            </Button>
          </div>
          
          <div className="flex flex-col items-center space-y-2">
            <Button
              variant="secondary"
              onClick={triggerFileUpload}
            >
              {t('CONFIG', 'CONFIG_UPLOAD', 'Upload from file')}
             </Button>
             {/* Input file caché */}
             <input
               ref={fileInputRef}
               type="file"
               accept=".json"
               style={{ display: 'none' }}
               onChange={handleUploadConfigFile}
             />
          </div>
        </div>

        {/* Message Display */}
        <MessageLog 
          key={message.key}
          type={message.type} 
          text={message.text} 
          autoHide={true} 
          duration={5000}
        />
      </form>
    </div>
  );
}
